import { useTranslation } from "react-i18next";
import { Progress } from "@tellescope/ui";
import type { TodayTotals, NutritionTargets } from "./useWeightMacroTrackerBlock";
import { mlToOz } from "../../../utils/nutritionUnitConversions";

interface TodaySummaryProps {
  totals: TodayTotals;
  targets: NutritionTargets;
}

interface SummaryRowProps {
  label: string;
  value: number;
  target: number | undefined;
  unit: string;
}

const SummaryRow = ({ label, value, target, unit }: SummaryRowProps) => {
  const { t } = useTranslation("portal");
  if (!target) return null;
  const percentage = Math.min(100, Math.round((value / target) * 100));

  return (
    <div className="space-y-1.5">
      <div className="flex items-baseline justify-between text-sm">
        <span className="font-medium">{label}</span>
        <span className="text-muted-foreground">
          {t("{{value}} / {{target}} {{unit}}", { value: Math.round(value), target: Math.round(target), unit })}
        </span>
      </div>
      <Progress value={percentage} />
    </div>
  );
};

export const TodaySummary = ({ totals, targets }: TodaySummaryProps) => {
  const { t } = useTranslation("portal");

  const hasAnyTarget =
    targets.dailyCalorieTarget ||
    targets.dailyProteinTarget ||
    targets.dailyCarbTarget ||
    targets.dailyFatTarget ||
    targets.dailyFiberTarget ||
    targets.dailyWaterTarget;

  if (!hasAnyTarget) return null;

  return (
    <div className="space-y-3 rounded-lg border bg-card p-4">
      <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{t("Today")}</p>
      <SummaryRow label={t("Calories")} value={totals.calories} target={targets.dailyCalorieTarget} unit="kcal" />
      <SummaryRow label={t("Protein")} value={totals.protein} target={targets.dailyProteinTarget} unit="g" />
      <SummaryRow label={t("Carbs")} value={totals.carbs} target={targets.dailyCarbTarget} unit="g" />
      <SummaryRow label={t("Fat")} value={totals.fat} target={targets.dailyFatTarget} unit="g" />
      <SummaryRow label={t("Fiber")} value={totals.fiber} target={targets.dailyFiberTarget} unit="g" />
      <SummaryRow
        label={t("Water")}
        value={mlToOz(totals.water)}
        target={targets.dailyWaterTarget ? mlToOz(targets.dailyWaterTarget) : undefined}
        unit="oz"
      />
    </div>
  );
};
